import { DateTime } from 'luxon'
import { apiAxios } from '@/api'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { LIMITED_HISTORY_ANSWER_PATH } from '../../lib/constants'
import { SLOT_KIND, type Slot } from './statementUpload'

/**
 * The months a start answer can record: the server's not-open run, cut at a month
 * the card shows as no longer open, and only when the card shows one of them.
 */
export const toStartCandidates = (accountSlots: readonly Slot[]): string[] => {
  const shown = new Map(accountSlots.map((slot) => [slot.month, slot]))
  const candidates: string[] = []
  for (const month of accountSlots[0]?.notOpenRun ?? []) {
    const slot = shown.get(month)
    if (slot && slot.state.kind !== SLOT_KIND.open) break
    candidates.push(month)
  }
  return candidates.some((month) => shown.has(month)) ? candidates : []
}

const fromYearMonth = (month: string) =>
  DateTime.fromFormat(month, DATE_FORMATS_LUXON.YEAR_MONTH, { zone: 'utc' })

/** Names months by month alone, or with the year when the months span more than one. */
export const toMonthNamer = (months: readonly string[]) => {
  const spansYears = new Set(months.map((month) => month.slice(0, 4))).size > 1
  const format = spansYears
    ? DATE_FORMATS_LUXON.MONTH_YEAR_LONG
    : DATE_FORMATS_LUXON.MONTH
  return {
    spansYears,
    name: (month: string): string => fromYearMonth(month).toFormat(format),
  }
}

/** The month the bank's history starts in, or the one after the last candidate when the day is not known yet. */
export const toBankStartMonth = (
  candidates: readonly string[],
  bankHistoryStartsOn: string | null
): string =>
  bankHistoryStartsOn
    ? DateTime.fromFormat(
        bankHistoryStartsOn,
        DATE_FORMATS_LUXON.YEAR_MONTH_DAY,
        { zone: 'utc' }
      ).toFormat(DATE_FORMATS_LUXON.YEAR_MONTH)
    : fromYearMonth(candidates[candidates.length - 1])
        .plus({ months: 1 })
        .toFormat(DATE_FORMATS_LUXON.YEAR_MONTH)

export const monthsBefore = (
  candidates: readonly string[],
  startMonth: string
): string[] => candidates.filter((month) => month < startMonth)

export const saveStartMonth = async (
  financialAccountId: number,
  notOpenMonths: readonly string[]
): Promise<void> => {
  await apiAxios.put(LIMITED_HISTORY_ANSWER_PATH, {
    financialAccountId,
    notOpenMonths,
  })
}
